/** @jsxImportSource @emotion/react */
import { useState, useRef, useEffect, Fragment } from "react";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { accessAPI, Boton } from "../../shared";
import MensajeAsistente from "./MensajeAsistente";
import MemoriaAsistente from "./MemoriaAsistente";
import {
  usePantallaActual,
  avisarDestacadosActualizados,
  ANCHO_LATERAL,
  MEDIA_MOBILE
} from "./ContextoAsistente";
import BotonDestacar from "./BotonDestacar";

// La API responde enseguida y genera la respuesta en segundo plano (una
// consulta compleja supera los 30 s que Heroku espera por una request). El
// panel consulta el estado del mensaje cada este intervalo hasta que termina.
const INTERVALO_CONSULTA_RESPUESTA = 2500;

// El campo de la pregunta crece con el texto hasta esta cantidad de líneas;
// a partir de ahí scrollea.
const MAX_LINEAS_PREGUNTA = 4;

// Cada cuánto se revisa si llegó un resumen del diario sin leer.
const INTERVALO_NO_LEIDAS = 10 * 60 * 1000;

const TITULOS = {
  chat: "Asistente",
  conversaciones: "Conversaciones",
  memoria: "Lo que sé de vos"
};

// Asistente personal del admin, disponible en todas las pantallas. Envuelve a
// toda la app: con el panel abierto, la app queda a la izquierda (se achica,
// no queda tapada) y el panel a la derecha, fijo al alto de la ventana, así
// que se puede seguir usando la plataforma con el asistente abierto. En mobile
// el panel ocupa toda la pantalla. Vive fuera de las rutas: la conversación
// sigue al navegar. Con cada pregunta manda la entidad que está en pantalla
// (ver ContextoAsistente).
export default function AsistenteLateral({ children }) {
  const [abierto, setAbierto] = useState(false);
  const [vista, setVista] = useState("chat");
  const [conversacionId, setConversacionId] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [pregunta, setPregunta] = useState("");
  const [cargando, setCargando] = useState(false);
  const [conversaciones, setConversaciones] = useState([]);
  const [cargandoLista, setCargandoLista] = useState(false);
  // Ítems que el diario ocultó por las preferencias del usuario
  const [ocultos, setOcultos] = useState([]);
  const [verOcultos, setVerOcultos] = useState(false);
  const [noLeidas, setNoLeidas] = useState({ cantidad: 0, ultimaId: null });
  // Mensaje del asistente que se está generando en la conversación abierta
  const [mensajePendienteId, setMensajePendienteId] = useState(null);

  const pantalla = usePantallaActual();
  const toast = useRef(null);
  const listaMensajesRef = useRef(null);
  const inputRef = useRef(null);

  // Mantiene la conversación scrolleada al último mensaje
  useEffect(() => {
    if (listaMensajesRef.current) {
      listaMensajesRef.current.scrollTop =
        listaMensajesRef.current.scrollHeight;
    }
  }, [mensajes, cargando, vista, abierto]);

  useEffect(() => {
    if (abierto && vista === "chat") inputRef.current?.focus();
  }, [abierto, vista]);

  // Ajusta el alto del campo al texto: de 1 a MAX_LINEAS_PREGUNTA líneas.
  // Se mide con height "auto" para que también se achique al borrar.
  useEffect(() => {
    const campo = inputRef.current;
    if (!campo) return;
    const estilo = window.getComputedStyle(campo);
    const alturaLinea = parseFloat(estilo.lineHeight) || 20;
    const extra =
      parseFloat(estilo.paddingTop) +
      parseFloat(estilo.paddingBottom) +
      parseFloat(estilo.borderTopWidth) +
      parseFloat(estilo.borderBottomWidth);
    const maximo = alturaLinea * MAX_LINEAS_PREGUNTA + extra;
    campo.style.height = "auto";
    const necesaria = campo.scrollHeight + parseFloat(estilo.borderTopWidth) + parseFloat(estilo.borderBottomWidth);
    campo.style.height = `${Math.min(necesaria, maximo)}px`;
    campo.style.overflowY = necesaria > maximo ? "auto" : "hidden";
  }, [pregunta, abierto, vista]);

  function mostrarError(error, textoPorDefecto) {
    toast.current?.show({
      severity: "error",
      summary: "Error",
      detail: error?.[0]?.msg || textoPorDefecto,
      sticky: true
    });
  }

  function enviar(e) {
    e.preventDefault();
    if (!pregunta.trim() || cargando) return;

    const preguntaEnviada = pregunta.trim();
    setMensajes((prev) => [...prev, { rol: "user", texto: preguntaEnviada }]);
    setPregunta("");
    setCargando(true);

    accessAPI(
      "POST",
      "admin/asistente",
      { prompt: preguntaEnviada, conversacionId, contexto: pantalla },
      (respuesta) => {
        // La pregunta ya quedó guardada; la respuesta llega por consulta.
        setConversacionId(respuesta.conversacionId);
        setMensajes((prev) => [
          ...prev,
          {
            id: respuesta.mensajeId,
            rol: "assistant",
            estado: "PROCESANDO",
            texto: ""
          }
        ]);
        setMensajePendienteId(respuesta.mensajeId);
      },
      (error) => {
        // Si el pedido no llegó a guardarse, se saca la pregunta de la vista y
        // se devuelve al input para reintentar.
        setMensajes((prev) => prev.slice(0, -1));
        setPregunta(preguntaEnviada);
        setCargando(false);
        mostrarError(
          error,
          "Ocurrió un error al procesar tu pregunta. Intenta nuevamente."
        );
      }
    );
  }

  // Consulta la respuesta pendiente hasta que deja de estar PROCESANDO. Si el
  // usuario cambia de conversación, deja de consultar: la respuesta se sigue
  // generando igual y aparece al volver a abrirla.
  useEffect(() => {
    if (!mensajePendienteId) return;
    let vigente = true;
    const intervalo = setInterval(() => {
      accessAPI(
        "GET",
        `admin/asistente/mensajes/${mensajePendienteId}`,
        null,
        (mensaje) => {
          if (!vigente || mensaje.estado === "PROCESANDO") return;
          setMensajes((prev) =>
            prev.map((m) =>
              m.id === mensaje.id
                ? { ...m, estado: mensaje.estado, texto: mensaje.texto }
                : m
            )
          );
          setMensajePendienteId(null);
          setCargando(false);
          // La respuesta pudo crear o quitar destacados (herramienta destacar)
          avisarDestacadosActualizados();
        },
        // Un fallo puntual de red no corta la espera: se reintenta en el
        // próximo intervalo.
        () => {}
      );
    }, INTERVALO_CONSULTA_RESPUESTA);
    return () => {
      vigente = false;
      clearInterval(intervalo);
    };
  }, [mensajePendienteId]);

  function cargarNoLeidas() {
    accessAPI(
      "GET",
      "admin/asistente/conversaciones/no-leidas",
      null,
      (respuesta) => setNoLeidas(respuesta),
      () => {}
    );
  }

  useEffect(() => {
    cargarNoLeidas();
    const intervalo = setInterval(cargarNoLeidas, INTERVALO_NO_LEIDAS);
    return () => clearInterval(intervalo);
  }, []);

  // Al abrir el overlay sin una conversación en curso, si hay un resumen del
  // diario sin leer, se abre ese.
  function alternarOverlay() {
    const abriendo = !abierto;
    setAbierto(abriendo);
    if (abriendo && !mensajes.length && noLeidas.ultimaId) {
      abrirConversacion({ id: noLeidas.ultimaId });
    }
  }

  // En mobile el panel tapa la pantalla: al seguir un link se cierra para
  // que se vea la página. En desktop queda abierto al lado.
  function cerrarEnMobile() {
    if (window.matchMedia("(max-width: 767px)").matches) setAbierto(false);
  }

  function nuevaConversacion() {
    setConversacionId(null);
    setMensajePendienteId(null);
    setCargando(false);
    setOcultos([]);
    setVerOcultos(false);
    setMensajes([]);
    setPregunta("");
    setVista("chat");
  }

  function verConversaciones() {
    setVista("conversaciones");
    setCargandoLista(true);
    accessAPI(
      "GET",
      "admin/asistente/conversaciones",
      null,
      (respuesta) => {
        setConversaciones(respuesta);
        setCargandoLista(false);
      },
      (error) => {
        setCargandoLista(false);
        mostrarError(error, "No se pudieron cargar las conversaciones.");
      }
    );
  }

  function abrirConversacion(conversacion) {
    setCargandoLista(true);
    accessAPI(
      "GET",
      `admin/asistente/conversaciones/${conversacion.id}`,
      null,
      (respuesta) => {
        setConversacionId(respuesta.id);
        const mensajesAbiertos = respuesta.mensajes.map((m) => ({
          id: m.id,
          rol: m.rol,
          estado: m.estado,
          texto: m.texto
        }));
        setMensajes(mensajesAbiertos);
        // Si la conversación tiene una respuesta generándose, se retoma la espera
        const pendiente = mensajesAbiertos.find(
          (m) => m.estado === "PROCESANDO"
        );
        setMensajePendienteId(pendiente ? pendiente.id : null);
        setCargando(!!pendiente);
        setOcultos(respuesta.ocultos || []);
        setVerOcultos(false);
        setPregunta("");
        setCargandoLista(false);
        setVista("chat");
        if (!respuesta.leida) {
          accessAPI(
            "PUT",
            `admin/asistente/conversaciones/${respuesta.id}/leida`,
            null,
            cargarNoLeidas,
            () => {}
          );
        }
      },
      (error) => {
        setCargandoLista(false);
        mostrarError(error, "No se pudo abrir la conversación.");
      }
    );
  }

  function borrarConversacion(id) {
    accessAPI(
      "DELETE",
      `admin/asistente/conversaciones/${id}`,
      null,
      () => {
        setConversaciones((prev) => prev.filter((c) => c.id !== id));
        // Si era la conversación abierta, el chat queda vacío
        if (id === conversacionId) nuevaConversacion();
        cargarNoLeidas();
      },
      (error) => mostrarError(error, "No se pudo borrar la conversación.")
    );
  }

  function formatearFecha(fecha) {
    return new Date(fecha).toLocaleDateString("es-UY", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  }

  return (
    <div css={{ display: "flex", alignItems: "flex-start" }}>
      <Toast ref={toast} position="top-center" />

      {/* La app: se achica cuando el panel está abierto */}
      <div css={{ flex: "1 1 auto", minWidth: 0 }}>{children}</div>

      <BotonDestacar toast={toast} lateralAbierto={abierto} />

      {!abierto && (
        <div
          css={{
            position: "fixed",
            right: "20px",
            // Por encima de la botonera inferior de mobile (NavLateral)
            bottom: "calc(20px + var(--alto-barra-inferior, 0px))",
            zIndex: 1001,
            "& .p-button": {
              width: "3.2rem",
              height: "3.2rem",
              padding: 0,
              justifyContent: "center",
              borderRadius: "50%",
              fontSize: "1.3rem",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)"
            }
          }}
        >
          <Boton
            badge={noLeidas.cantidad ? noLeidas.cantidad : null}
            callback={alternarOverlay}
          >
            <i className="pi pi-comments"></i>
          </Boton>
        </div>
      )}

      {abierto && (
        <aside
          css={{
            position: "sticky",
            top: 0,
            flex: `0 0 ${ANCHO_LATERAL}px`,
            width: `${ANCHO_LATERAL}px`,
            height: "100vh",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "white",
            borderLeft: "1px solid #e5e5e5",
            boxShadow: "-4px 0 16px rgba(0, 0, 0, 0.06)",
            [MEDIA_MOBILE]: {
              position: "fixed",
              inset: 0,
              width: "100%",
              height: "100dvh",
              borderLeft: "none"
            }
          }}
        >
          <div
            css={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              padding: "10px 12px",
              borderBottom: "1px solid #e5e5e5"
            }}
          >
            <strong css={{ fontSize: "1.1rem", color: "var(--grisTextos)" }}>
              {TITULOS[vista]}
            </strong>
            <div css={{ display: "flex", gap: "4px" }}>
              {vista === "chat" ? (
                <>
                  <Boton icono callback={() => setVista("memoria")}>
                    <i className="pi pi-bookmark"></i>
                  </Boton>
                  <Boton icono callback={verConversaciones}>
                    <i className="pi pi-history"></i>
                  </Boton>
                </>
              ) : (
                <Boton icono callback={() => setVista("chat")}>
                  <i className="pi pi-arrow-left"></i>
                </Boton>
              )}
              <Boton
                icono
                disabled={vista === "chat" && !mensajes.length}
                callback={nuevaConversacion}
              >
                <i className="pi pi-plus"></i>
              </Boton>
              <span
                css={{
                  "& .p-button, & .p-button:enabled:hover": {
                    color: "var(--grisTextos)"
                  }
                }}
              >
                <Boton icono callback={() => setAbierto(false)}>
                  <i className="pi pi-times"></i>
                </Boton>
              </span>
            </div>
          </div>

          {vista === "memoria" ? (
            <div css={{ flex: 1, overflowY: "auto" }}>
              <MemoriaAsistente mostrarError={mostrarError} />
            </div>
          ) : vista === "conversaciones" ? (
            <div css={{ flex: 1, overflowY: "auto", padding: "8px" }}>
              {cargandoLista && (
                <div css={{ padding: "12px", color: "var(--grisTextos)" }}>
                  <i className="pi pi-spin pi-spinner"></i>
                </div>
              )}
              {!cargandoLista &&
                conversaciones.map((conversacion) => (
                  <div
                    key={conversacion.id}
                    onClick={() => abrirConversacion(conversacion)}
                    css={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor:
                        conversacion.id === conversacionId
                          ? "#f2f2f2"
                          : "transparent",
                      "&:hover": { backgroundColor: "#f2f2f2" }
                    }}
                  >
                    <div css={{ flex: 1, minWidth: 0 }}>
                      <div
                        css={{
                          fontWeight: conversacion.leida ? 400 : 700,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {conversacion.titulo || "Conversación"}
                      </div>
                      <div
                        css={{
                          fontSize: "0.8rem",
                          color: "var(--grisTextos)"
                        }}
                      >
                        {formatearFecha(conversacion.fechaActualizacion)}
                      </div>
                    </div>
                    {/* El click de borrar no tiene que abrir la conversación */}
                    <span onClick={(e) => e.stopPropagation()}>
                      <Boton
                        icono
                        rojo
                        confirmacion="¿Borrar esta conversación?"
                        callback={() => borrarConversacion(conversacion.id)}
                      >
                        <i className="pi pi-trash"></i>
                      </Boton>
                    </span>
                  </div>
                ))}
            </div>
          ) : (
            <>
              <div
                ref={listaMensajesRef}
                css={{
                  flex: 1,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  padding: "12px",
                  // Cuando la conversación no entra, la lista scrollea en vez
                  // de achicar a sus hijos. Sin esto, el spinner (que tiene
                  // overflow hidden y por eso alto mínimo 0) quedaba aplastado
                  // a 0 px con la conversación llena.
                  "& > *": { flexShrink: 0 }
                }}
              >
                {mensajes.map((mensaje, index) => (
                  <Fragment key={mensaje.id || `local-${index}`}>
                    {/* El mensaje en proceso lo representa el spinner */}
                    {mensaje.estado !== "PROCESANDO" && (
                      <MensajeAsistente
                        rol={mensaje.rol}
                        texto={mensaje.texto}
                        error={mensaje.estado === "ERROR"}
                        onNavegar={cerrarEnMobile}
                      />
                    )}
                    {/* Los ocultos van debajo del resumen del diario */}
                    {index === 0 && ocultos.length > 0 && (
                      <div css={{ fontSize: "0.85rem" }}>
                        <Boton
                          texto
                          chico
                          callback={() => setVerOcultos(!verOcultos)}
                        >
                          <i
                            className={`pi ${
                              verOcultos ? "pi-chevron-down" : "pi-chevron-right"
                            }`}
                            css={{ marginRight: "0.4em" }}
                          ></i>
                          {`Oculté ${ocultos.length} ${
                            ocultos.length === 1 ? "ítem" : "ítems"
                          } por tus preferencias`}
                        </Boton>
                        {verOcultos && (
                          <ul
                            css={{
                              margin: "4px 0 0 0",
                              paddingLeft: "1.4em",
                              color: "var(--grisTextos)"
                            }}
                          >
                            {ocultos.map((oculto, i) => (
                              <li key={i}>
                                {oculto.texto}
                                {oculto.motivo && ` — ${oculto.motivo}`}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </Fragment>
                ))}

                {cargando && (
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      // El spinner rota con transform y su caja rotada agranda
                      // el área scrolleable en cada frame: sin overflow hidden
                      // titila la barra de scroll.
                      overflow: "hidden",
                      color: "var(--grisTextos)"
                    }}
                  >
                    <i className="pi pi-spin pi-spinner"></i>
                    <span>Procesando tu pregunta...</span>
                  </div>
                )}
              </div>

              <form
                onSubmit={enviar}
                css={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "flex-end",
                  padding: "10px 12px",
                  borderTop: "1px solid #e5e5e5"
                }}
              >
                <InputTextarea
                  ref={inputRef}
                  rows={1}
                  value={pregunta}
                  onChange={(e) => setPregunta(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter envía; Shift+Enter agrega una línea
                    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                      enviar(e);
                    }
                  }}
                  disabled={cargando}
                  css={{ flex: 1, minWidth: 0, resize: "none", lineHeight: 1.4 }}
                />
                <Boton
                  type="submit"
                  icono
                  disabled={!pregunta.trim() || cargando}
                >
                  <i className="pi pi-send"></i>
                </Boton>
              </form>
            </>
          )}
        </aside>
      )}
    </div>
  );
}
